import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, CheckCircle, Clock, MapPin, ExternalLink, Copy, ShoppingBag, AlertCircle, Loader2, Download, Printer } from 'lucide-react';
import Layout from '../components/layout/Layout';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const OrdersPage = () => {
  const navigate = useNavigate();
  const { orders, user, isAuthenticated, addTrackingInfo, confirmDelivery, confirmPickup, refreshUserData } = useApp();
  const [activeTab, setActiveTab] = useState('buying');
  const [showTrackingModal, setShowTrackingModal] = useState(null);
  const [shippingLoading, setShippingLoading] = useState({});
  const [trackingForm, setTrackingForm] = useState({
    trackingNumber: '',
    trackingUrl: '',
    shippingMethod: 'Australia Post'
  });

  // Force fresh fetch of orders every time this page is visited
  useEffect(() => {
    if (user?.id && refreshUserData) {
      refreshUserData(user.id);
    }
  }, [user?.id]);

  if (!isAuthenticated) {
    return (
      <Layout title="Orders">
        <div className="flex flex-col items-center justify-center py-20 px-4" data-testid="orders-login-prompt">
          <Package className="w-16 h-16 text-gray-300 mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Sign in to view orders</h2>
          <p className="text-gray-500 text-center mb-4">Track your purchases and sales</p>
          <Button onClick={() => navigate('/login')} className="bg-minted-600 hover:bg-minted-700" data-testid="orders-login-btn">
            Sign In
          </Button>
        </div>
      </Layout>
    );
  }

  const buyingOrders = orders.filter(o => (o.buyer_id || o.buyerId) === user?.id);
  const sellingOrders = orders.filter(o => (o.seller_id || o.sellerId) === user?.id);

  const handleShipWithShippit = async (order) => {
    const orderId = order.id;
    setShippingLoading(prev => ({ ...prev, [orderId]: true }));
    try {
      const res = await fetch(`${API_BASE}/api/orders/${orderId}/shipping/create-shipment/${user.id}`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Shipment created! Tracking: ${data.tracking_number}`);
        if (data.label_url) {
          window.open(data.label_url, '_blank');
        }
        if (refreshUserData) refreshUserData(user.id);
      } else {
        toast.error(data.error || data.detail || 'Failed to create shipment');
      }
    } catch (err) {
      toast.error('Failed to create shipment. Try again.');
    } finally {
      setShippingLoading(prev => ({ ...prev, [orderId]: false }));
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      pending: { cls: 'bg-yellow-100 text-yellow-700', label: 'Awaiting Shipment' },
      shipped: { cls: 'bg-blue-100 text-blue-700', label: 'Shipped' },
      delivered: { cls: 'bg-minted-50 text-minted-800', label: 'Delivered' },
      pickup_arranged: { cls: 'bg-purple-100 text-purple-700', label: 'Pickup Arranged' },
      completed: { cls: 'bg-minted-50 text-minted-800', label: 'Completed' },
      cancelled: { cls: 'bg-red-100 text-red-700', label: 'Cancelled' },
    };
    const s = map[status] || { cls: 'bg-gray-100 text-gray-600', label: status };
    return <Badge className={s.cls} data-testid="order-status-badge">{s.label}</Badge>;
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'shipped': return <Truck className="w-5 h-5 text-blue-500" />;
      case 'delivered':
      case 'completed': return <CheckCircle className="w-5 h-5 text-stone-700" />;
      case 'pickup_arranged': return <MapPin className="w-5 h-5 text-purple-500" />;
      case 'cancelled': return <AlertCircle className="w-5 h-5 text-red-500" />;
      default: return <Package className="w-5 h-5 text-gray-500" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return ''; }
  };

  const handleAddTracking = async (orderId) => {
    const result = await addTrackingInfo(orderId, trackingForm.trackingNumber, trackingForm.trackingUrl, trackingForm.shippingMethod);
    if (result?.success) {
      toast.success('Tracking info added! Buyer has been notified.');
    } else {
      toast.error('Failed to add tracking. Please try again.');
    }
    setShowTrackingModal(null);
    setTrackingForm({ trackingNumber: '', trackingUrl: '', shippingMethod: 'Australia Post' });
  };

  const copyTracking = (trackingNumber) => {
    navigator.clipboard.writeText(trackingNumber);
    toast.success('Tracking number copied!');
  };

  const getExpectedDelivery = (order) => {
    if (order.status === 'completed' || order.status === 'delivered') return 'Delivered';
    if (order.status === 'pickup_arranged') return 'Arrange pickup with seller';
    if (order.status === 'shipped') {
      const shipped = order.shippedAt || order.shipped_at;
      if (shipped) {
        const est = new Date(new Date(shipped).getTime() + 5 * 24 * 60 * 60 * 1000);
        return `Est. ${formatDate(est.toISOString())}`;
      }
      return 'In transit';
    }
    if (order.status === 'pending') {
      const method = order.shippingMethod || order.shipping_method;
      return method === 'Pickup' ? 'Arrange pickup with seller' : 'Seller preparing shipment';
    }
    return '';
  };

  const OrderCard = ({ order, isSeller }) => {
    const title = order.productTitle || order.product_title || 'Item';
    const image = order.productImage || order.product_image;
    const otherName = isSeller 
      ? (order.buyerName || order.buyer_name || 'Buyer') 
      : (order.sellerName || order.seller_name || 'Seller');
    const price = parseFloat(order.totalPrice || order.total_price || 0);
    const orderId = order.id || '';
    const createdAt = order.createdAt || order.created_at;
    const method = order.shippingMethod || order.shipping_method || 'Shipping';

    return (
      <div className="bg-white rounded-sm border border-stone-100 shadow-sm overflow-hidden mb-3" data-testid={`order-card-${orderId}`}>
        <div className="p-4">
          {/* Header: status + date */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              {getStatusIcon(order.status)}
              {getStatusBadge(order.status)}
            </div>
            <span className="text-xs text-gray-400">{formatDate(createdAt)}</span>
          </div>

          {/* Product Info */}
          <div className="flex gap-3 mb-3">
            {image ? (
              <img src={image} alt={title} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
            ) : (
              <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                <ShoppingBag className="w-6 h-6 text-gray-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-gray-900 text-sm line-clamp-1" data-testid="order-product-title">{title}</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {isSeller ? 'Buyer' : 'Seller'}: {otherName}
              </p>
              <p className="text-base font-bold text-stone-900 mt-1" data-testid="order-price">${price.toFixed(2)}</p>
            </div>
          </div>

          {/* Delivery info */}
          <div className="bg-gray-50 rounded-lg p-2.5 mb-3 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">{method === 'Pickup' ? 'Collection' : 'Delivery'}</span>
              <span className="font-medium text-gray-700" data-testid="order-delivery-est">{getExpectedDelivery(order)}</span>
            </div>
            {orderId && (
              <div className="flex justify-between mt-1">
                <span className="text-gray-500">Order</span>
                <span className="font-mono text-gray-500">#{orderId.slice(0, 8)}</span>
              </div>
            )}
          </div>

          {/* Tracking Info - only show for non-cancelled orders */}
          {order.status !== 'cancelled' && (order.trackingNumber || order.tracking_number) && (
            <div className="bg-blue-50 rounded-lg p-3 mb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-blue-600 font-medium">Tracking ({order.shippingMethod || order.shipping_method || 'Post'})</p>
                  <p className="font-mono text-sm font-medium text-blue-900">{order.trackingNumber || order.tracking_number}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => copyTracking(order.trackingNumber || order.tracking_number)} data-testid="copy-tracking-btn">
                    <Copy className="w-4 h-4" />
                  </Button>
                  {(order.trackingUrl || order.tracking_url) && (
                    <Button variant="ghost" size="sm" onClick={() => window.open(order.trackingUrl || order.tracking_url, '_blank')} data-testid="track-link-btn">
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 flex-wrap">
            {/* Seller: Ship with Shippit (auto-generate label) */}
            {isSeller && order.status === 'pending' && method !== 'Pickup' && (
              <>
                <Button
                  className="flex-1 bg-minted-800 hover:bg-minted-700 text-white"
                  size="sm"
                  onClick={() => handleShipWithShippit(order)}
                  disabled={shippingLoading[orderId]}
                  data-testid="ship-with-shippit-btn"
                >
                  {shippingLoading[orderId] 
                    ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Creating...</>
                    : <><Printer className="w-4 h-4 mr-1" /> Generate Label</>
                  }
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowTrackingModal(order)}
                  data-testid="manual-tracking-btn"
                  className="text-xs"
                >
                  Manual
                </Button>
              </>
            )}
            {/* Seller: Download label if already shipped */}
            {isSeller && order.status === 'shipped' && (order.shipping?.label_url || order.label_url) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(order.shipping?.label_url || order.label_url, '_blank')}
                data-testid="download-label-btn"
              >
                <Download className="w-4 h-4 mr-1" /> Label
              </Button>
            )}
            {!isSeller && order.status === 'shipped' && (
              <Button className="flex-1 bg-minted-800 hover:bg-minted-700 text-white" size="sm" data-testid="confirm-received-btn"
                onClick={async () => { 
                  const result = await confirmDelivery(order.id); 
                  if (result?.success) toast.success('Delivery confirmed! Funds released to seller.');
                  else toast.error('Failed to confirm delivery.');
                }}>
                <CheckCircle className="w-4 h-4 mr-1" /> Confirm Received
              </Button>
            )}
            {!isSeller && order.status === 'pickup_arranged' && (
              <Button className="flex-1 bg-minted-800 hover:bg-minted-700 text-white" size="sm" data-testid="confirm-collected-btn"
                onClick={async () => { 
                  const result = await confirmPickup(order.id); 
                  if (result?.success) toast.success('Pickup confirmed! Funds released to seller.');
                  else toast.error('Failed to confirm pickup.');
                }}>
                <CheckCircle className="w-4 h-4 mr-1" /> Confirm Collected
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => navigate('/messages')} data-testid="order-message-btn">
              Message
            </Button>
            {order.status !== 'cancelled' && (
              <Button variant="outline" size="sm" onClick={() => navigate(`/order/${orderId}`)} data-testid="order-details-btn">
                Track Order
              </Button>
            )}
            {order.status === 'cancelled' && (
              <div className="w-full text-center text-xs text-red-500 font-medium py-1">
                This order has been cancelled
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Layout title="Orders">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="orders-tabs">
        <TabsList className="w-full rounded-none border-b bg-white">
          <TabsTrigger value="buying" className="flex-1" data-testid="buying-tab">Buying ({buyingOrders.length})</TabsTrigger>
          <TabsTrigger value="selling" className="flex-1" data-testid="selling-tab">Selling ({sellingOrders.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="buying" className="p-4" data-testid="buying-orders-list">
          {buyingOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16" data-testid="no-purchases">
              <Package className="w-12 h-12 text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">No purchases yet</p>
              <p className="text-sm text-gray-400 mt-1">Items you buy will appear here</p>
              <Button variant="link" className="text-stone-900 mt-3" onClick={() => navigate('/')}>
                Start Shopping
              </Button>
            </div>
          ) : (
            buyingOrders.map(order => <OrderCard key={order.id} order={order} isSeller={false} />)
          )}
        </TabsContent>

        <TabsContent value="selling" className="p-4" data-testid="selling-orders-list">
          {sellingOrders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16" data-testid="no-sales">
              <Package className="w-12 h-12 text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">No sales yet</p>
              <p className="text-sm text-gray-400 mt-1">When someone buys your item, it shows here</p>
              <Button variant="link" className="text-stone-900 mt-3" onClick={() => navigate('/sell')}>
                Create a Listing
              </Button>
            </div>
          ) : (
            sellingOrders.map(order => <OrderCard key={order.id} order={order} isSeller={true} />)
          )}
        </TabsContent>
      </Tabs>

      {/* Add Tracking Modal */}
      <Dialog open={!!showTrackingModal} onOpenChange={() => setShowTrackingModal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Tracking Information</DialogTitle>
            <DialogDescription>Enter the shipping details so the buyer can track their order</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Shipping Method</Label>
              <Input value={trackingForm.shippingMethod} onChange={(e) => setTrackingForm({ ...trackingForm, shippingMethod: e.target.value })} placeholder="e.g., Australia Post" className="mt-1" data-testid="tracking-method-input" />
            </div>
            <div>
              <Label>Tracking Number</Label>
              <Input value={trackingForm.trackingNumber} onChange={(e) => setTrackingForm({ ...trackingForm, trackingNumber: e.target.value })} placeholder="e.g., AU123456789" className="mt-1" data-testid="tracking-number-input" />
            </div>
            <div>
              <Label>Tracking URL (optional)</Label>
              <Input value={trackingForm.trackingUrl} onChange={(e) => setTrackingForm({ ...trackingForm, trackingUrl: e.target.value })} placeholder="https://..." className="mt-1" data-testid="tracking-url-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTrackingModal(null)}>Cancel</Button>
            <Button className="bg-minted-800 hover:bg-minted-700" onClick={() => handleAddTracking(showTrackingModal?.id)} disabled={!trackingForm.trackingNumber} data-testid="save-tracking-btn">
              Save Tracking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default OrdersPage;
